import React from "react";
import { useI18n } from "@/i18n/LanguageContext";

// Single source of truth for the contact number used across WhatsApp / Viber / tel: links.
export const PHONE_E164 = "+306948559619";
export const PHONE_DISPLAY = "+30 694 855 9619";
const PHONE_DIGITS = "306948559619";

const DEFAULT_WA_MESSAGE_EN = "Hello, I'm interested in one of your vehicles.";

export function buildWhatsAppUrl(message) {
  return `https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent(
    message || DEFAULT_WA_MESSAGE_EN
  )}`;
}

export function buildViberUrl() {
  // Universal Viber deep link — opens the chat in Viber on mobile, falls back to
  // the user's installed Viber app on desktop. The %2B preserves the leading "+".
  return `viber://chat?number=%2B${PHONE_DIGITS}`;
}

export const WHATSAPP_URL = buildWhatsAppUrl(DEFAULT_WA_MESSAGE_EN);
export const VIBER_URL = buildViberUrl();

export function WhatsAppGlyph({ size = 22 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.11 4.91A9.93 9.93 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.92c0 1.75.46 3.46 1.34 4.97L2 22l5.25-1.37a9.9 9.9 0 0 0 4.78 1.22h.01c5.46 0 9.91-4.45 9.91-9.92 0-2.65-1.03-5.14-2.84-7.02zm-7.07 15.27h-.01a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.4c0-4.55 3.71-8.25 8.26-8.25a8.2 8.2 0 0 1 5.84 2.42 8.2 8.2 0 0 1 2.41 5.84c0 4.55-3.71 8.25-8.25 8.25zm4.53-6.17c-.25-.12-1.47-.73-1.7-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.98-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.77-1.84-.2-.48-.41-.41-.56-.42l-.48-.01c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.75.59.25 1.05.4 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.1-.22-.16-.47-.28z" />
    </svg>
  );
}

export function ViberGlyph({ size = 22 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12.5 2C8.36 2 4.44 2.55 3.06 5.27 2.06 7.27 2 9.55 2 11.66c0 2.11.06 4.39 1.06 6.4.96 1.92 3.31 2.59 6.04 2.83-.07.51.06 1.27.83 1.93.85.73 1.8.79 1.8.79v-1.7s.04-.7-.27-.83c.95.04 1.81.05 2.54.05 4.14 0 8.06-.55 9.44-3.27 1-2 1.06-4.29 1.06-6.4 0-2.11-.06-4.39-1.06-6.4C22.06 2.55 18.14 2 14 2h-1.5zM9.6 6.21c.18-.04.36 0 .49.12.32.27.61.61.91 1 .27.34.5.71.65 1.05.16.36.06.74-.22.94l-.5.36c-.12.08-.16.24-.11.37.31.74.69 1.43 1.16 2.07.46.64 1.02 1.21 1.65 1.7.12.09.28.08.39-.02l.42-.43c.23-.24.59-.34.92-.21.36.13.7.32 1.05.55.36.24.69.49 1 .77.13.12.18.3.13.46-.16.54-.49 1.04-.94 1.4-.74.59-1.72.77-2.64.5-1.5-.43-2.83-1.27-3.93-2.34-1.1-1.07-2-2.37-2.5-3.84-.31-.91-.18-1.92.39-2.68.34-.45.83-.79 1.38-.95v.18zm5.84.59c1.5 0 2.93.62 3.97 1.62.97.97 1.54 2.29 1.55 3.71v.07a.45.45 0 01-.49.45.45.45 0 01-.41-.45c-.01-1.21-.49-2.34-1.32-3.16-.93-.93-2.18-1.45-3.48-1.45-.16 0-.32 0-.48.02a.45.45 0 11-.07-.9c.25-.02.49-.02.73-.02v.11zm.18 1.66c1.93 0 3.5 1.55 3.5 3.45.01.25-.19.46-.43.46h-.04a.45.45 0 01-.43-.43c-.05-1.4-1.19-2.54-2.6-2.59a.45.45 0 010-.9v.01zm.27 1.65c.86.13 1.51.78 1.65 1.65a.45.45 0 11-.9.13 1.04 1.04 0 00-.86-.85.45.45 0 11.11-.93z" />
    </svg>
  );
}

export function ContactFloats() {
  const { t } = useI18n();
  const waHref = buildWhatsAppUrl(t("whatsapp.message"));
  const viberHref = buildViberUrl();
  return (
    <div className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 flex flex-col items-end gap-3">
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float"
        aria-label="Contact us on WhatsApp"
        className="group flex items-center gap-3 bg-[#0B0B0B] border border-[#C6A15B]/40 text-[#C6A15B] hover:text-black hover:bg-[#C6A15B] transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)] px-4 py-3 md:px-5 md:py-3.5"
      >
        <span className="relative flex items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-[#C6A15B]/20 animate-ping group-hover:hidden" />
          <WhatsAppGlyph size={20} />
        </span>
        <span className="hidden md:inline text-[11px] tracking-[0.26em] uppercase font-medium">
          {t("whatsapp.label")}
        </span>
      </a>
      <a
        href={viberHref}
        data-testid="viber-float"
        aria-label="Contact us on Viber"
        className="group flex items-center gap-3 bg-[#0B0B0B] border border-[#C6A15B]/40 text-[#C6A15B] hover:text-black hover:bg-[#C6A15B] transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)] px-4 py-3 md:px-5 md:py-3.5"
      >
        <ViberGlyph size={20} />
        <span className="hidden md:inline text-[11px] tracking-[0.26em] uppercase font-medium">
          {t("viber.label")}
        </span>
      </a>
    </div>
  );
}
